<template>
  <div class="tickets" v-if="typeof currentEvent !== 'undefined'">
    <Breadcrumbs v-if="!isHome">
      <router-link :to="{ name: 'tickets' }" class="active">{{
        getNavName('NAV_TICKETS')
      }}</router-link>
      <span v-if="this.$route.params.id">{{ currentEvent.name }}</span>
    </Breadcrumbs>
    <div class="pageBlock">
      <div class="pageHeader">
        <button class="backButton" type="button" v-if="!isHome" @click="this.$router.go(-1)">
          {{ $t('NAV_BACK') }}
        </button>
        <h1>
          <span class="eventName">{{ currentEvent.name }}</span>
        </h1>
        <div class="venue">{{ currentEvent.venue }}</div>
        <div v-if="showHeader === false" class="goToCheckout">
          <router-link :to="{ name: 'checkout' }">
            <icon icon="shoppingBag"></icon> {{ $t('SHOPPING_CART') }}
          </router-link>
        </div>
      </div>
      <div class="ticketBlock">
        <form v-if="presaleNotExpired">
          <div><Loading class="loading" :visible="loadingSingle" size="small"></Loading></div>
          <transition name="fade">
            <DatePicker
              class="datePicker"
              v-if="shouldShowCalendar"
              fname="datePicker"
              v-model="selectedDay"
              :availableDates="calendarDates"
              :soldOutDates="soldoutDates"
              v-on:dateChange="updateCalendar"
              label="TICKETS_SELECT_A_DATE"
            ></DatePicker>
          </transition>
          <transition name="fade">
            <div
              v-if="shouldShowCalendar && ticketsForDate.length !== 0"
              v-show="shouldShowTimePicker"
              class="timePicker"
            >
              <span v-for="timeslotId in timeslotsOrder" :key="timeslotId">
                <button
                  class="timeBtn"
                  @click="setTime(timeslotId, $event)"
                  v-bind:class="{
                    selected: form.time === timeslotsForDate[timeslotId].datetime,
                    notAvailable: timeslotsForDate[timeslotId].max_tickets <= 0,
                  }"
                  :disabled="timeslotsForDate[timeslotId].max_tickets <= 0"
                  :title="unavailableTimeTitle(timeslotId)"
                >
                  {{ getTime(timeslotsForDate[timeslotId].datetime) }}
                </button>
              </span>
            </div>
          </transition>
          <Alert :condition="ticketsForDate.length === 0 && !loadingSingle">
            {{ $t('TICKETS_TIME_UNAVAILABLE') }}
          </Alert>

          <TicketGroups />
          <div v-if="ticketsForDate && showTickets" class="tickets">
            <DateTickets :tickets="ticketsForDate" />
          </div>

          <Alert :condition="!isFamilyTicketReady()">
            {{ $t('TICKETS_WRONG_FAMILY_TICKET_COUNT') }}
          </Alert>
          <Alert :condition="buyingMoreTicketsThanAvailable && maxTicketsForSchedule > 0">
            {{ $t('TICKETS_OVERBOOKING_MSG_PART1') }} {{ form.ticketCount }}
            {{ $t('TICKETS_OVERBOOKING_MSG_PART2') }} {{ maxTicketsForSchedule }}
            {{ $t('TICKETS_OVERBOOKING_MSG_PART3') }}
          </Alert>
          <div class="ticketFooter">
            <div class="totalPrice">
              <div class="name">
                {{ $t('TICKETS_TOTAL') }}
                <span class="vatMessage">({{ $t('ALL_PRICES_INCLUDE_VAT') }})</span>
              </div>
              <div class="value">{{ formatCurrency(form.totalPrice) }}</div>
            </div>

            <div class="actions addToCart">
              <button
                :disabled="
                  !(buyEnabled() && isFamilyTicketReady() && !buyingMoreTicketsThanAvailable)
                "
                @click.stop.prevent="add()"
                v-bind:class="{ hasTickets: totalTickets > 0 }"
                class="mainAction"
              >
                {{ $t('TICKETS_BUY_PREFIX') }} {{ totalTicketsWithSubTickets }}
                <span v-if="totalTickets === 1">{{ $t('TICKETS_TICKET') }}</span>
                <span v-else>{{ $t('TICKETS_TICKETS') }}</span>
                <span v-if="totalTickets !== 0">
                  {{ $t('TICKETS_BUY_SUFFIX') }}
                </span>
                <span v-if="selectedDay && shouldShowCalendar && !areAllSelectedTicketsMode3">
                  ({{ formatDate(selectedDay) }})</span
                >
              </button>
            </div>
          </div>
        </form>
        <Alert :condition="!presaleNotExpired">
          {{ currentEvent.endPreSaleText }}
        </Alert>

        <div class="imageContainer">
          <ImageC
            class="ticketImage"
            v-if="currentEvent.event_image"
            :srcSuffix="currentEvent.event_image"
            @load="imageLoaded"
          />
        </div>
        <p class="ticketDescription">
          <button
            class="showInfoImage"
            v-if="currentEvent.imageLocationUrl"
            @click="showInfoImage = !showInfoImage"
          >
            {{ currentEvent.imageLocationButtonCaption }}
          </button>
          <span v-html="currentEvent.description"></span>
        </p>
      </div>
    </div>
    <OverlayWindow v-if="showInfoImage" v-on:done="showInfoImage = !showInfoImage">
      <template v-slot:title>{{ currentEvent.imageLocationButtonCaption }}</template>
      <ImageC :srcSuffix="currentEvent.imageLocationUrl" @load="imageLoaded" />
      <div>{{ currentEvent.imageLocationCaption }}</div>
    </OverlayWindow>

    <teleport to="head">
      <template v-for="jsonld in jsonlds">
        <component :is="'script'" type="application/ld+json">
          {{ jsonld }}
        </component>
      </template>
    </teleport>
  </div>
  <div v-else>
    <ExpiredEvent></ExpiredEvent>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import moment from 'moment-mini';
import Breadcrumbs from '../Breadcrumbs.vue';
import DatePicker from '../Form/DatePicker.vue';
import ExpiredEvent from './ExpiredEvent.vue';
import DateTickets from './DateTickets.vue';
import TicketGroups from './TicketGroups.vue';
import Loading from '../Loading.vue';
import Icon from '../Icon.vue';
import formater from '../../lib/formater';
import { setPageTitle } from '../../lib/pageTitle';
import strip from '../../lib/stripHtml';
import ImageC from '../ImageC.vue';
import Alert from '../Alert.vue';
import OverlayWindow from '../OverlayWindow/OverlayWindow.vue';
import { useSeoMeta } from '@unhead/vue';

export default {
  name: 'EventComponent',
  components: {
    Alert,
    Breadcrumbs,
    DatePicker,
    DateTickets,
    Loading,
    ExpiredEvent,
    Icon,
    ImageC,
    TicketGroups,
    OverlayWindow,
  },
  data() {
    return {
      showCalendar: false,
      showTickets: false,
      showInfoImage: false,
    };
  },
  computed: {
    ...mapState({
      loadingList: (state) => state.tickets.loadingList,
      loadedList: (state) => state.tickets.loadedList,
      events: (state) => state.tickets.data.events,
      showHeader: (state) => state.app.showHeader,
      loadingSingle: (state) => state.tickets.loadingSingle,
      calendarDates: (state) => Object.keys(state.tickets.data.singleCalendar),
      schedule: (state) => state.tickets.data.schedule,
      timeslotsForDate: (state) => state.tickets.data.timeslotsForDate,
      timeslotsOrder: (state) => state.tickets.data.timeslotsOrder,
      ticketsForDate: (state) => state.tickets.data.ticketsForDate,
      timeOptions: (state) => state.tickets.form.timeOptions,
      form: (state) => state.tickets.form,
      totalTickets: (state) => state.tickets.form.ticketCount,
      publicURL: (state) => state.app.API_PUBLIC_URL,
      soldoutDates: (state) => state.tickets.data.soldoutDates,
      lifetimeStart: (state) => state.tickets.data.lifetime.start,
      maxTicketsForSchedule: (state) => state.tickets.data.maxTicketsForSchedule,
      currencySymbol: (state) => state.app.currencySymbol,
      siteName: (state) => state.app.siteName,
      museumMainUrl: (state) => state.app.museumMainUrl,
      navTranslations: (state) => state.app.navTranslations,
      language: (state) => state.language,
    }),
    ...mapGetters({
      totalTicketsWithSubTickets: 'tickets/totalTicketsWithSubTickets',
    }),
    jsonlds() {
      const occurrences = [];
      if (typeof this.ticketsForDate[0] !== 'undefined') {
        this.ticketsForDate.forEach((ticket) => {
          Object.keys(this.timeslotsForDate).forEach((k) => {
            const timeslot = this.timeslotsForDate[k];
            const start = timeslot.datetime.replace(' ', 'T');
            let availability = 'https://schema.org/InStock';

            const occurence = {
              '@context': 'https://schema.org/',
              '@type': 'Event',
              name: `${this.currentEvent.name} - ${ticket.name}`,
              image: this.currentEvent.event_image,
              description: strip(this.currentEvent.description),
              organizer: {
                '@type': 'Organization',
                name: this.siteName,
                url: this.museumMainUrl,
              },
              startDate: start,
              //"endDate": "2025-07-21T23:00-05:00",
              eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
              location: {
                '@type': 'Place',
                name: this.siteName,
              },
              offers: {
                '@type': 'Offer',
                url: window.location,
                price: (ticket.price / 100).toFixed(2),
                priceCurrency: this.currencySymbol,
                availability: availability,
                //"validFrom": "2024-05-21T12:00"
              },
            };
            occurrences.push(occurence);
          });
        });
      }

      return occurrences;
    },

    selectedDay: {
      get() {
        return this.$store.state.tickets.form.date;
      },
      set(v) {
        this.setDay(v);
      },
    },
    presaleNotExpired() {
      if (
        this.currentEvent.endPreSaleDate === null ||
        typeof this.currentEvent.endPreSaleDate === 'undefined'
      ) {
        return true;
      }
      const m = moment(this.currentEvent.endPreSaleDate, 'YYYY-MM-DD hh:mm:ss');
      if (m.isBefore(moment())) {
        return false;
      }
      return true;
    },
    currentEvent() {
      let { id } = this.$route.params;
      if (this.form.eventId !== null) {
        id = this.form.eventId;
      }
      if (!this.loadedList || !id) {
        return {};
      }
      return this.events[id];
    },
    areAllSelectedTicketsMode3() {
      let returnVal = true;
      Object.keys(this.$store.state.tickets.form.tickets).find((k) => {
        if (this.$store.state.tickets.form.tickets[k].count > 0) {
          this.$store.state.tickets.data.ticketsForDate.find((ticketForDate) => {
            if (ticketForDate.id === k) {
              if (parseInt(ticketForDate.mode, 10) !== 3) {
                returnVal = false;
                return true;
              }
            }
          });
        }
        return !returnVal;
      });
      return returnVal;
    },
    shouldShowCalendar() {
      if (
        import.meta.env.VITE_SITE_FRONT_ID === 'dpm' ||
        import.meta.env.VITE_SITE_FRONT_ID === 'devdpm'
      ) {
        return false;
      }
      if (
        (import.meta.env.VITE_SITE_FRONT_ID === 'dmh' ||
          import.meta.env.VITE_SITE_FRONT_ID === 'devdmh') &&
        parseInt(this.currentEvent.id, 10) === 1428
      ) {
        return false;
      }
      if (
        (import.meta.env.VITE_SITE_FRONT_ID === 'fvv' ||
          import.meta.env.VITE_SITE_FRONT_ID === 'devfvv') &&
        parseInt(this.currentEvent.id, 10) === 33
      ) {
        return false;
      }
      if (
        (import.meta.env.VITE_SITE_FRONT_ID === 'kwb' ||
          import.meta.env.VITE_SITE_FRONT_ID === 'devkwb') &&
        parseInt(this.currentEvent.id, 10) === 1989
      ) {
        return false;
      }
      if (
        (import.meta.env.VITE_SITE_FRONT_ID === 'mf' ||
          import.meta.env.VITE_SITE_FRONT_ID === 'devmf') &&
        parseInt(this.currentEvent.id, 10) === 8
      ) {
        return false;
      }
      if (
        (import.meta.env.VITE_SITE_FRONT_ID === 'mf' ||
          import.meta.env.VITE_SITE_FRONT_ID === 'devmf') &&
        parseInt(this.currentEvent.id, 10) === 14
      ) {
        return false;
      }
      if (
        (import.meta.env.VITE_SITE_FRONT_ID === 'smk' ||
          import.meta.env.VITE_SITE_FRONT_ID === 'devsmk') &&
        parseInt(this.currentEvent.id, 10) === 1848
      ) {
        return false;
      }
      if (
        (import.meta.env.VITE_SITE_FRONT_ID === 'hlmd' ||
          import.meta.env.VITE_SITE_FRONT_ID === 'devhlmd') &&
        parseInt(this.currentEvent.id, 10) === 1387
      ) {
        return false;
      }
      if (
        (import.meta.env.VITE_SITE_FRONT_ID === 'vdhm' ||
          import.meta.env.VITE_SITE_FRONT_ID === 'devdhm') &&
        parseInt(this.currentEvent.id, 10) === 2319
      ) {
        return false;
      }
      if (
        (import.meta.env.VITE_SITE_FRONT_ID === 'hlmd' ||
          import.meta.env.VITE_SITE_FRONT_ID === 'devhlmd') &&
        (parseInt(this.currentEvent.id, 10) === 1946 || parseInt(this.currentEvent.id, 10) === 1948)
      ) {
        return false;
      }

      if (this.calendarDates.length > 0 || this.showCalendar) {
        // review this
        this.showCalendar = true; /* eslint-disable-line */
        return true;
      }
      return false;
    },
    shouldShowTimePicker() {
      const ticketModes = [];
      if (!Array.isArray(this.ticketsForDate)) {
        return false;
      }
      this.ticketsForDate.forEach((t) => {
        if (!ticketModes.includes(parseInt(t.mode, 10))) {
          ticketModes.push(parseInt(t.mode, 10));
        }
      });
      if (ticketModes.length === 1 && ticketModes[0] === 5) {
        return false;
      }
      return true;
    },
    buyingMoreTicketsThanAvailable() {
      if (this.maxTicketsForSchedule === null) {
        return false;
      }
      return this.maxTicketsForSchedule < this.form.ticketCount;
    },
  },
  props: {
    isHome: { default: false },
    exhibitionId: { default: null },
  },
  methods: {
    ...mapActions({
      setLoadingSingle: 'tickets/setLoadingSingle',
      addToCart: 'tickets/addToCart',
      clearForm: 'tickets/clearForm',
      clearTickets: 'tickets/clearTickets',
      getTicketList: 'tickets/getTicketList',
      getList: 'tickets/getList',
      getSingleCalendar: 'tickets/getSingleCalendar',
      setEventId: 'tickets/setEventId',
      setFormDate: 'tickets/setFormDate',
      setTicketsForDateFormTime: 'tickets/setTicketsForDateFormTime',
      getLifetime: 'tickets/getLifetime',
    }),
    getNavName(type) {
      if (typeof this.navTranslations !== 'undefined') {
        if (typeof this.navTranslations[type] !== 'undefined') {
          if (
            typeof this.navTranslations[type][this.language] !== 'undefined' &&
            this.navTranslations[type][this.language] !== ''
          ) {
            return this.navTranslations[type][this.language];
          }
        }
      }
      return this.$t(type);
    },
    add() {
      this.addToCart().then(() => {
        this.clearForm();
        this.$toasted.show(this.$t('TICKETS_ADDED_TO_CART')).goAway(2000);
        this.$router.push({ name: 'checkout' });
      });
    },
    buyEnabled() {
      if (this.totalTickets === 0) {
        return false;
      }
      return true;
    },
    getTime(d) {
      const t = d.split(' ')[1];
      const tArr = t.split(':');
      return `${tArr[0]}:${tArr[1]}`;
    },
    unavailableTimeTitle(timeslotId) {
      if (this.timeslotsForDate[timeslotId].max_tickets <= 0) {
        return this.$t('TICKETS_TIME_UNAVAILABLE');
      }
      return '';
    },
    getCalendar(newDate, endDate) {
      return new Promise((resolve) => {
        const date = moment();
        if (typeof newDate !== 'undefined') {
          if (typeof newDate.month !== 'undefined') {
            date.month(newDate.month);
          }
          if (typeof newDate.year !== 'undefined') {
            date.year(newDate.year);
          }
          if (typeof newDate.day !== 'undefined') {
            date.date(newDate.day);
          }
        }
        let id = null;
        if (typeof this.$route.params.id !== 'undefined') {
          ({ id } = this.$route.params);
        } else if (this.form.eventId !== null) {
          id = this.form.eventId;
        }
        if (typeof id === 'undefined') {
          [id] = Object.keys(this.eventList);
        }
        this.setEventId(parseInt(id, 10));
        this.getSingleCalendar({ exhibition: id, date, endDate }).then(() => {
          this.getTicketList(id).then(() => {
            resolve();
          });
        });
      });
    },
    setDay(date) {
      this.setFormDate(date).then(() => {
        let id = null;
        if (typeof this.$route.params.id !== 'undefined') {
          ({ id } = this.$route.params);
        } else if (this.form.eventId !== null) {
          id = this.form.eventId;
        }
        this.getTicketList(id).then(() => {});
        const query = structuredClone(this.$route.query);
        query.date = date;
        this.$router.replace({ query });
      });
    },
    setTime(timeslotId, event) {
      if (event) event.preventDefault();
      this.setTicketsForDateFormTime(timeslotId);
    },
    updateCalendar(date) {
      let d = date.day;
      /* if (date.day === 0) {
        d = 1;
      } */
      d = 1;
      this.getCalendar({ year: date.year, month: date.month, day: d });
    },
    formatCurrency(val) {
      return formater.currency(val);
    },
    formatDate(val) {
      return formater.date(val);
    },
    isFamilyTicketReady() {
      let familyTickets = [];
      if (typeof this.ticketsForDate.length !== 'undefined' && this.ticketsForDate.length !== 0) {
        familyTickets = this.ticketsForDate.filter((t) => {
          if (typeof t.family === 'undefined') {
            return false;
          }
          return t.family;
        });
      }
      if (familyTickets.length === 0) {
        return true;
      }
      let returnVal = null;
      let emptyFamily = true;
      let wrongQuantities = false;
      familyTickets.forEach((ft) => {
        emptyFamily = true;
        const { id } = ft;
        wrongQuantities = false;
        if (returnVal !== false) {
          // each subticketId

          if (typeof this.form.tickets[id] !== 'undefined') {
            Object.keys(this.form.tickets[id].subTickets).forEach((subTicketId) => {
              const subTicket = this.form.tickets[id].subTickets[subTicketId];
              if (subTicket.count !== 0) {
                emptyFamily = false;
              }
            });
            if (!emptyFamily) {
              Object.keys(this.form.tickets[id].subTickets).forEach((subTicketId) => {
                const subTicket = this.form.tickets[id].subTickets[subTicketId];
                const subTicketData = ft.subtickets.find((st) => subTicketId === st.id);
                if (typeof subTicketData !== 'undefined') {
                  if (subTicket.count < parseInt(subTicketData.min_quantity, 10)) {
                    wrongQuantities = true;
                  }
                  if (subTicket.count > parseInt(subTicketData.max_quantity, 10)) {
                    wrongQuantities = true;
                  }
                }
              });
            }
          }
          if (emptyFamily) {
            returnVal = true;
          } else if (!wrongQuantities) {
            returnVal = true;
          } else if (wrongQuantities) {
            returnVal = false;
          }
        }
      });
      return returnVal;
    },
    imageLoaded(e) {
      if (typeof e.classList !== 'undefined') {
        e.classList.add('loaded');
      }
    },
    setMeta() {
      let title = '';
      let description = '';
      let image = '';
      description = strip(this.currentEvent.description);
      if (this.currentEvent.metadata) {
        description = strip(this.currentEvent.metadatas);
      }
      title = setPageTitle(this.currentEvent.name);
      if (['bo', 'devbo'].includes(import.meta.env.VITE_SITE_FRONT_ID)) {
        title = setPageTitle('Ticket Shop Aussichtsplattform');
      }

      if (this.currentEvent.image_url) {
        image = this.currentEvent.image_url;
        image = image.replace('%WIDTH%', '1200');
      }
      title = this.currentEvent.name;

      useSeoMeta({
        title: title,
        ogTitle: title,
        description,
        ogDescription: description,
        ogImage: image,
      });
    },
  },
  mounted() {
    this.setLoadingSingle(true);
    this.showCalendar = false;
    this.$route.meta.title = this.$t('TICKETS_PAGE_TITLE');
    this.clearTickets().then(() => {
      this.showTickets = true;
    });
    if (!this.loadedEvents) {
      this.getList()
        .then(() => {
          if (typeof this.currentEvent.id === 'undefined') {
            this.$router.replace({ name: 'pageNotFound' });
          }
          //const ticketsTranslation = this.$t('TICKETS_PAGE_TITLE');
          this.setMeta();
        })
        .catch(() => {});
    }

    this.clearForm();

    let eventId = null;
    if (typeof this.$route.params.id !== 'undefined') {
      eventId = this.$route.params.id;
    } else if (this.exhibitionId !== null) {
      eventId = this.exhibitionId;
    }
    if (eventId === null) {
      [eventId] = Object.keys(this.eventList);
    }
    this.setEventId(parseInt(eventId, 10));

    this.getLifetime().then(() => {
      let d;
      let endDate = moment().add(1, 'month').endOf('month');
      let m = moment();
      if (typeof this.$route.query.date !== 'undefined') {
        m = moment(this.$route.query.date, 'YYYY-MM-DD');
        if (m.isValid()) {
          if (m.isBefore(moment())) {
            m = moment();
          }
          d = { year: m.year(), month: m.month(), day: m.date() };
          endDate = m.add(1, 'month').endOf('month');
        }
      }
      const s = moment(this.lifetimeStart);
      if (s.isAfter(m)) {
        d = { year: s.year(), month: s.month(), day: s.date() };
        endDate = s.add(1, 'month').endOf('month');
      }
      this.getCalendar(d, endDate).then(() => {
        let t = false;
        if (typeof this.$route.query.time !== 'undefined') {
          t = this.$route.query.time.replace('-', ':');
        }
        const { timeslotsForDate } = this;
        const timeslotId = Object.keys(timeslotsForDate).find((id) => {
          const timeslot = timeslotsForDate[id];
          const timeStr = timeslot.datetime.split(' ')[1];
          const timeArr = timeStr.split(':');
          const time = `${timeArr[0]}:${timeArr[1]}`;
          return time === t;
        });
        if (typeof timeslotId !== 'undefined') {
          if (parseInt(timeslotsForDate[timeslotId].max_tickets, 10) > 0) {
            this.setTicketsForDateFormTime(timeslotId);
          }
        } else {
          // set first available time
          if (typeof this.timeslotsOrder[0] !== 'undefined') {
            this.setTicketsForDateFormTime(this.timeslotsOrder[0]);
          }
        }
      });
    });
  },
};

/*metaInfo() { I don't think this is being used anymore
    const ticketsTranslation = this.$t('TICKETS_PAGE_TITLE');
    let description = '';
    let title = '';
    const { currentEvent } = this;
    if (typeof currentEvent !== 'undefined') {
      description = strip(currentEvent.description);
      title = setPageTitle(`${currentEvent.name} - ${ticketsTranslation}`);
      if (['bo', 'devbo'].includes(import.meta.env.VITE_SITE_FRONT_ID)) {
        title = setPageTitle('Ticket Shop Aussichtsplattform');
      }
    }
    return {
      title,
      meta: [{
        vmid: 'description',
        name: 'description',
        content: `${description}`,
      }],
    };
  },*/
</script>

<style lang="scss" scoped>
@import '../../scss/grid';
@import '../../scss/mixins';
@import '../../scss/buttons';
@import '../../scss/scaffolding'; // img-responsive

.pageHeader {
  display: flex;
  flex-wrap: wrap;
  h1 {
    margin-top: $sp * 3;
    width: 100%;
    flex-grow: 1;
  }
  .venue {
    width: 100%;
    flex-grow: 1;
  }
  .goToCheckout {
    flex-basis: auto;
    a {
      @extend .btn;
      @extend .btn-primary;
      margin-top: 20px;
    }
  }
}
.tickets {
  .pageBlock {
    background-color: $block-color;
    max-width: 1200px;
    margin-left: auto;
    margin-right: auto;
    @include blockPadding;
  }
  .ticket:nth-child(odd),
  .entryTicket {
    background-color: $secondary-block-color-1;
  }

  .ticket:nth-child(even) {
    background-color: $secondary-block-color-2;
  }

  .ticket,
  .entryTicket {
    padding-top: $sp;
    padding-bottom: calc($sp / 2);
    @extend .row;
    margin-left: 0;
    margin-right: 0;
  }
}
.addToCart {
  margin-top: 2 * $sp;
  text-align: center;
  .ticketTotal {
    display: none;
  }
  button {
    @extend .btn;
    @extend .btn-primary;
    @extend .btn-lg;
  }
}
.ticketBlock {
  margin-top: $sp;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 2 * $sp;
}
form {
  margin-bottom: 2 * $sp;
  grid-column: 1;
  grid-row: 1 / 30;
}
.imageContainer {
  margin-bottom: $sp;
  grid-column: 2;
  grid-row: 1 / 2;
  .ticketImage {
    @extend .img-responsive;
  }
}
.ticketDescription {
  grid-column: 2;
  grid-row: 2;
  align-self: start;
}
@media screen and (max-width: $screen-md) {
  .ticketBlock {
    grid-template-columns: 1fr;
  }
  form {
    grid-column: 1;
    grid-row: 3;
  }

  .imageContainer {
    grid-column: 1;
    grid-row: 1;
  }

  .ticketDescription {
    grid-column: 1;
    grid-row: 2;
  }
}
.timeBtn {
  @extend .btn;
  @extend .btn-default;
  margin-right: calc($sp/2);
  margin-bottom: calc($sp/2);
  min-width: 4.5em;
}
.timeBtn.selected {
  @extend .btn-primary;
}
.timeBtn:disabled {
  color: $brand-danger;
}

.totalPrice {
  margin-top: 3 * $sp;
  @extend .row;
  .name {
    @extend .col-xs-8;
    font-weight: 500;
  }
  .value {
    @extend .col-xs-4;
    text-align: right;
    white-space: nowrap;
  }
}
.results {
  @extend .col-md-6;
}

@media screen and (max-width: $screen-xs) {
  button.mainAction {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    border-radius: 0;
    width: 100%;
    z-index: 20;
    height: 62px;
    box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.5);
    opacity: 1;
    transition: all 0.5s;
  }
  button.mainAction:not(.hasTickets) {
    background-color: darken($secondary-block-color-2, 30%) !important;
  }

  .actions .ticketTotal {
    display: inline-block;
    margin-left: 1em;
  }
}

img {
  opacity: 0;
  transition: opacity 0.5s;
}
img.loaded {
  opacity: 1;
}

.fade-enter-active,
.fade-leave-active {
  transition: all 1s;
}
.fade-enter, .fade-leave-to /* .list-leave-active below version 2.1.8 */ {
  opacity: 0;
}

.loading {
  display: block;
  text-align: center;
}
.datePicker:deep(label) {
  font-size: 1.25em;
}
.showInfoImage {
  margin-top: $sp;
  margin-bottom: $sp;
}
</style>
